import type { ReactNode } from 'react';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { nip19 } from 'nostr-tools';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { NoteCard } from '@/components/portfolio/NoteCard';
import { useAuthor } from '@/hooks/useAuthor';
import NotFound from './NotFound';

function PageShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-2xl py-10">
        <Link
          to="/"
          className="mb-8 inline-flex items-center gap-2 rounded-full text-sm font-medium text-muted-foreground transition-colors hover:text-bitcoin focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ArrowLeft className="size-4" />
          Back to portfolio
        </Link>
        {children}
      </div>
    </div>
  );
}

function ProfileView({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;

  const npub = nip19.npubEncode(pubkey);
  const displayName = metadata?.display_name || metadata?.name || 'Anonymous';
  const initials = displayName.split(' ').map((w) => w[0]).slice(0, 2).join('');

  return (
    <PageShell>
      <Card>
        <CardContent className="space-y-4 p-6 sm:p-8">
          <div className="flex items-center gap-4">
            <Avatar className="size-16 border border-border">
              <AvatarImage src={metadata?.picture} alt={displayName} />
              <AvatarFallback className="bg-gradient-to-br from-bitcoin to-nostr text-xl font-bold text-white">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <h1 className="truncate font-display text-2xl font-bold">{displayName}</h1>
              {metadata?.nip05 && (
                <p className="truncate font-mono text-sm text-bitcoin">{metadata.nip05}</p>
              )}
            </div>
          </div>

          {metadata?.about && (
            <p className="leading-relaxed text-muted-foreground">{metadata.about}</p>
          )}

          <p className="break-all rounded-lg bg-muted p-3 font-mono text-xs text-muted-foreground">
            {npub}
          </p>

          <a
            href={`https://njump.me/${npub}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-bitcoin hover:underline"
          >
            View full profile on Nostr
          </a>
        </CardContent>
      </Card>
    </PageShell>
  );
}

function NoteView({ id }: { id: string }) {
  const { nostr } = useNostr();

  const { data: event, isLoading } = useQuery({
    queryKey: ['note-view', id],
    queryFn: async (c) => {
      const [event] = await nostr.query([{ ids: [id] }], { signal: c.signal });
      return event ?? null;
    },
  });

  return (
    <PageShell>
      {isLoading ? (
        <Card>
          <CardContent className="p-6">
            <div className="mb-3 flex items-center gap-3">
              <Skeleton className="size-10 rounded-full" />
              <div className="space-y-1.5">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </CardContent>
        </Card>
      ) : event ? (
        <NoteCard event={event} />
      ) : (
        <Card className="border-dashed">
          <CardContent className="py-12 px-8 text-center">
            <p className="mx-auto max-w-sm text-muted-foreground">
              This note couldn't be found on the connected relays.
            </p>
          </CardContent>
        </Card>
      )}
    </PageShell>
  );
}

export function NIP19Page() {
  const { nip19: identifier } = useParams<{ nip19: string }>();

  if (!identifier) {
    return <NotFound />;
  }

  let decoded;
  try {
    decoded = nip19.decode(identifier);
  } catch {
    return <NotFound />;
  }

  const { type } = decoded;

  switch (type) {
    case 'npub':
      return <ProfileView pubkey={decoded.data} />;

    case 'nprofile':
      return <ProfileView pubkey={decoded.data.pubkey} />;

    case 'note':
      return <NoteView id={decoded.data} />;

    case 'nevent':
      return <NoteView id={decoded.data.id} />;

    case 'naddr':
      return (
        <PageShell>
          <Card className="border-dashed">
            <CardContent className="py-12 px-8 text-center">
              <p className="mx-auto max-w-sm text-muted-foreground">
                Addressable events are best viewed in a full Nostr client.
              </p>
            </CardContent>
          </Card>
        </PageShell>
      );

    default:
      return <NotFound />;
  }
}
